import { useEffect, useMemo, useRef, useState } from 'preact/hooks'
import { useForm, Controller } from 'react-hook-form'
import { X, Mail, Send, Pencil, Eye, CircleAlert, TriangleAlert } from 'lucide-preact'
import type { TouchPointViewDto, EmailSendRuleDto, EmailComplianceClass } from '@/types/touchPoint'
import { EmailEngagementTarget, isRecipientAnchoredRule } from '@/types/touchPoint'
import { EmailSendRuleKind, EmailEventType, EmailOffsetUnit, TouchPointType, SplitTestWinMetric } from '@/types/touchPoint'
import type { AssetVersionSummaryDto, WebAssetListItemDto } from '@/api/asset'
import type { FileImportSummaryDto } from '@/api/webs'
import { assetApi } from '@/api/asset'
import { useEmailRuleLookup } from '@/hooks/useEmailRuleLookup'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'
import { formatEmailRule } from '@/utils/emailRuleFormatter'
import { resolveComplianceClass } from '@/utils/complianceClassResolver'
import { routes } from '@/utils/routes'
import { touchPointsApi } from '@/api/touchPoints'
import { useToast } from '@/hooks/useToast'
import { useUserProfile, USER_ROLES } from '@/hooks/useUserProfile'
import { useLocation } from 'preact-iso'
import { EmailAssetSelect } from './touchpoint-sections/EmailAssetSelect'
import { SentenceRuleEditor } from './touchpoint-sections/SentenceRuleEditor'
import { ScheduledSendTimeFields } from './touchpoint-sections/ScheduledSendTimeFields'
import { EmailCopyPreview } from '@/components/assets/EmailCopyPreview'
import { activationVersionState } from '@/utils/emailActivationVersion'
import { ActivationVersionNote } from './touchpoint-sections/ActivationVersionNote'
import { useVerifiedDomainsLookup } from '@/hooks/useVerifiedDomainsLookup'
import { useReplyToAddressesLookup } from '@/hooks/useReplyToAddressesLookup'
import { MultiSelect } from '@/components'
import { ConsentClassChip } from './touchpoint-sections/ConsentClassChip'
import { EngagementTargetSelect } from './touchpoint-sections/EngagementTargetSelect'
import { SettingsNudge } from './touchpoint-sections/SettingsNudge'
import { SegmentedToggle } from '@/components/common/SegmentedToggle'
import { ToggleRow } from '@/components/common/ToggleRow'
import { NumberInput } from '@/components/common/NumberInput'
import { RadioGroup } from '@/components/common/RadioGroup'
import { ActivationLockBanner } from './ActivationLockBanner'
import { isTerminalBlastState, dispatchedCount, type EmailBlastViewDto } from '@/types/emailBlast'
import type { SendingDomainForecastRequestDto } from '@/types/sendingDomains'
import { parseValidationErrors } from '@/utils/errorHandling'
import { useSendingDomainForecast } from './useSendingDomainForecast'
import {
  domainOptionLabel,
  isDomainSelectable,
  isSpreadByWarmup,
  readyDomainToSwitchTo,
  saveBlockedReason,
  saveConsequence,
  willFireSpreadLine,
} from './sendingDomainForecastText'
import {
  RegistrantEmailsNote,
  SendingDomainRefusalBanner,
  SendingDomainSpreadBanner,
  SendingDomainStateLines,
} from './touchpoint-sections/SendingDomainWarmupNotes'

const DEFAULT_RULE: EmailSendRuleDto = {
  Kind: EmailSendRuleKind.Event,
  SendAtAbsolute: null,
  EventType: EmailEventType.WebRegistered,
  OffsetValue: 0,
  OffsetUnit: EmailOffsetUnit.Day,
}

// Form fields use '' (not null) for "none" so the native select shows its placeholder option and
// `required` rejects the empty value cleanly. ComplianceClass keeps null (server smart-defaults).
interface ActivationFormData {
  EmailAssetId: string
  FromSendingDomainId: string
  ComplianceClass: EmailComplianceClass | null
  EngagementTarget: EmailEngagementTarget | null
  ReplyToEmail: string
  TargetFileImportIds: string[]
  Rule: EmailSendRuleDto
  SplitTestEnabled: boolean
  SplitTestCohortPercent: number
  SplitTestWindowHours: number
  SplitTestWinMetric: SplitTestWinMetric
}

function buildDefaults(touchPoint: TouchPointViewDto | null, allFileImportIds: string[]): ActivationFormData {
  const props = touchPoint?.EmailProperties
  return {
    EmailAssetId: props?.EmailAssetId ?? '',
    FromSendingDomainId: props?.FromSendingDomainId ?? '',
    ComplianceClass: props?.ComplianceClass ?? null,
    EngagementTarget: props?.EngagementTarget ?? null,
    ReplyToEmail: props?.ReplyToEmail ?? '',
    // The selector shows what will actually be targeted. A NEW send starts with no list checked so the
    // audience is an explicit choice (ORB-443); editing a send saved as "all" (null) seeds every list,
    // and a persisted subset seeds just those. The save path collapses an all-checked selection back to
    // null (ORB-347, ORB-378).
    TargetFileImportIds: seedTargetFileImportIds(props?.TargetFileImportIds, allFileImportIds, !!touchPoint),
    Rule: props?.Rule ?? DEFAULT_RULE,
    SplitTestEnabled: props?.SubjectSplitTest?.Enabled ?? false,
    SplitTestCohortPercent: props?.SubjectSplitTest?.TestCohortPercent ?? 20,
    SplitTestWindowHours: props?.SubjectSplitTest?.MeasurementWindowMinutes
      ? Math.max(1, Math.round(props.SubjectSplitTest.MeasurementWindowMinutes / 60))
      : 1,
    SplitTestWinMetric: props?.SubjectSplitTest?.WinMetric ?? SplitTestWinMetric.Opens,
  }
}

/**
 * Seed the audience selection (ORB-347, ORB-443):
 * - A persisted subset seeds just those ids (dropping any no longer on the Web).
 * - Editing a send saved as "all" (null/empty) seeds every id — its audience was already decided,
 *   so reopening it never forces a re-pick.
 * - A NEW send starts EMPTY so the audience is an explicit choice, not a pre-checked default. The
 *   sole list on a single-list Web is auto-selected — there is nothing to choose.
 */
function seedTargetFileImportIds(
  persisted: string[] | null | undefined,
  allFileImportIds: string[],
  isEdit: boolean
): string[] {
  const filtered = (persisted ?? []).filter(id => allFileImportIds.includes(id))
  if (filtered.length > 0) return filtered
  if (isEdit) return allFileImportIds
  return allFileImportIds.length <= 1 ? allFileImportIds : []
}

// The SentenceRuleEditor self-corrects almost everything (mode switches seed defaults, offsets
// clamp, illegal "before" is disabled). This only guards the two reachable bad states — and
// surfaces them instead of letting the save 422 get swallowed.
function validateRule(rule: EmailSendRuleDto): true | string {
  if (rule.Kind === EmailSendRuleKind.Scheduled) {
    // ORB-352: Scheduled timing is now a local wall-clock + IANA zone pair; the server derives
    // the absolute instant. Both halves must be present.
    const localTime = rule.ScheduledSendLocalTime ?? ''
    if (!localTime || localTime.startsWith('T')) return 'Pick a send date and time.'
    if (!rule.ScheduledSendTimeZone) return 'Pick a timezone.'
    return true
  }
  if (rule.Kind === EmailSendRuleKind.Event && !rule.EventType) {
    return 'Pick an event to anchor to.'
  }
  return true
}

interface EmailActivationPanelProps {
  isOpen: boolean
  touchPoint: TouchPointViewDto | null
  clientCompanyId: string
  workspaceId: string
  campaignId: string
  webId: string
  /** Asset ids already bound to other email touchpoints on this Web — fed
   *  through to the picker so it can tag "already used" options. */
  usedAssetIds?: ReadonlySet<string>
  /** The parent Web's included file imports — options for the per-touchpoint target select (ORB-347). */
  includedFileImports?: FileImportSummaryDto[]
  /** IANA zone seeded as the default for a Scheduled rule's timezone (ORB-352). */
  defaultTimeZone?: string
  onClose: () => void
  onSaved: () => void
  onNavigateToAssets?: () => void
  /** The staged/in-flight blast for this activation, if any (ORB-378). Its presence locks the editor. */
  blast?: EmailBlastViewDto | null
  /** Cancel the staged send and reset the activation to editable (pre-send only). */
  onStartOver?: () => void
  /** Re-arm a disarmed (started-over) activation so it sends at its time. */
  onReschedule?: () => void
  /** In-flight flags for the lifecycle actions (spinner + disable). */
  startingOver?: boolean
  rescheduling?: boolean
}

export function EmailActivationPanel({
  isOpen,
  touchPoint,
  clientCompanyId,
  workspaceId,
  campaignId,
  webId,
  usedAssetIds,
  includedFileImports,
  defaultTimeZone,
  onClose,
  onSaved,
  onNavigateToAssets,
  blast,
  onStartOver,
  onReschedule,
  startingOver,
  rescheduling,
}: EmailActivationPanelProps) {
  const isEdit = !!touchPoint
  // showErrorUnless422 for anything that toasts an API response: apiClient already routes a 422 to
  // the global validation handler, which toasts each ValidationError on its own, so a local toast on
  // the same response is always a duplicate (ORB-538). Plain showError stays for the messages this
  // panel composes itself, which have no response behind them.
  const { showError, showErrorUnless422, showSuccess } = useToast()

  // Lock state (ORB-378): a staged/in-flight blast freezes the activation; a disarmed (started-over)
  // touchpoint shows a reschedule prompt instead. "Pre-send" is approximated from stats — the server is
  // the real guard (StartOver 409s if anything has dispatched).
  const lockedByBlast = !!blast
  const isDisarmed = !lockedByBlast && touchPoint?.EmailProperties?.Armed === false
  const blastTerminal = blast ? isTerminalBlastState(blast.State) : false
  const dispatchedApprox = blast ? dispatchedCount(blast.Stats) : 0
  const canStartOver = lockedByBlast && !blastTerminal && dispatchedApprox === 0
  // Deep link to this blast's Broadcasts detail (ORB-393): surfaced from the lock banner so the operator
  // can jump straight to the live send instead of hunting for it in the Broadcasts tab.
  const blastHref = blast
    ? routes.scoped(clientCompanyId, workspaceId).webBroadcast(campaignId, webId, blast.Id)
    : null

  // All of the parent Web's included file-import ids — the full audience. Used to collapse an
  // all-checked save back to null (= "all, dynamically") and to drive the audience summary.
  // Computed before useForm so it can seed the defaults.
  const allFileImportIds = useMemo(
    () => (includedFileImports ?? []).map(fi => fi.Id),
    [includedFileImports]
  )

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    getValues,
    formState: { isValid, isSubmitting, errors },
  } = useForm<ActivationFormData>({ mode: 'onChange', defaultValues: buildDefaults(touchPoint, allFileImportIds) })

  // UI-only state (not form fields): the picked asset object drives the auto-name; the applied
  // default (if any) drives the "default for X" caption — shown whenever the rule still equals it.
  const [selectedAsset, setSelectedAsset] = useState<WebAssetListItemDto | null>(null)
  const [appliedDefault, setAppliedDefault] = useState<{ emailTypeKey: string; rule: EmailSendRuleDto } | null>(null)
  const [sendingTest, setSendingTest] = useState(false)
  // The picked asset's EmailTypeKey — drives the conditional engagement gate (ORB-365). On create it's
  // captured when the asset is picked; on edit it's fetched once for the already-bound asset. null when
  // no asset is picked or the type couldn't be resolved.
  const [emailTypeKey, setEmailTypeKey] = useState<string | null>(null)
  // Whether the picked copy has a second subject (ORB-453) — gates the Subject A/B test section; null
  // until the asset detail loads.
  const [hasSecondSubject, setHasSecondSubject] = useState<boolean | null>(null)
  // Single-pane view switch — only takes effect below md (rail + preview sit side-by-side at md+).
  const [view, setView] = useState<'edit' | 'preview'>('edit')
  // What Save's last 422 said. The global handler shows it as a toast that disappears; this keeps it on
  // screen until the next attempt (ORB-953: the domain can change state between the forecast and Save).
  const [saveRefusal, setSaveRefusal] = useState<string[] | null>(null)
  const domainSelectRef = useRef<HTMLSelectElement>(null)

  const assetId = watch('EmailAssetId')
  const fromSendingDomainId = watch('FromSendingDomainId')
  const complianceClass = watch('ComplianceClass')
  const engagementTarget = watch('EngagementTarget')
  const replyToEmail = watch('ReplyToEmail')
  const targetFileImportIds = watch('TargetFileImportIds')
  const rule = watch('Rule')
  const splitTestEnabled = watch('SplitTestEnabled')
  const splitTestCohortPercent = watch('SplitTestCohortPercent')
  const splitTestWindowHours = watch('SplitTestWindowHours')
  const splitTestWinMetric = watch('SplitTestWinMetric')

  const lookup = useEmailRuleLookup(workspaceId, webId)
  const domains = useVerifiedDomainsLookup(workspaceId)
  const replyToAddresses = useReplyToAddressesLookup(workspaceId)
  const { userProfile, userCompanyRoleSets, hasCompanyRole } = useUserProfile()
  const recipientEmail = userProfile?.PrimaryEmail
  const { route } = useLocation()
  // Company-admin check (mirrors SettingsNavigation): Sending Domains and Reply-To Addresses are
  // admin-only settings screens (non-admins 403 on their APIs), so only admins get an actionable link
  // from the empty-state hints below — everyone else is told to ask an admin.
  const isCompanyAdmin =
    (userCompanyRoleSets?.find(roleSet => roleSet.ClientCompanyId === clientCompanyId)?.IsAdmin ?? false) ||
    hasCompanyRole(USER_ROLES.ADMIN, clientCompanyId)
  const goToSettings = (section: string) => route(`/company/${clientCompanyId}/settings/${section}`)
  const panelRef = useRef<HTMLDivElement>(null)

  // Target options come straight off the parent Web's included file imports (ORB-347).
  const fileImportOptions = useMemo(
    () => (includedFileImports ?? []).map(fi => ({ value: fi.Id, label: fi.Name || 'Unnamed import' })),
    [includedFileImports]
  )

  // Audience summary state for the "Who it targets" section (ORB-443): a new multi-list send starts
  // empty (must be picked), a full selection reads as "all N" (and persists as dynamic-all), and a
  // strict subset reads as "K of N".
  const totalListCount = allFileImportIds.length
  const selectedListCount = targetFileImportIds?.length ?? 0
  const allListsSelected =
    selectedListCount > 0 && sameStringSet(targetFileImportIds ?? [], allFileImportIds)

  // Locks body scroll while open and hides the AI chat widget — matches other overlays.
  useBodyScrollLock(isOpen)

  // Engagement gate (ORB-359/365) only applies to nurture-capable email types — those with a non-None
  // default in the EmailRules lookup. Anything else hides the gate AND keeps EngagementTarget null.
  //
  // ORB-508 adds a second condition on the RULE, not the type. The gate is applied while building an
  // audience, so on a per-recipient anchor — one that fires for one person at the instant they
  // submitted a form or clicked an email — there is no audience to narrow and the server now rejects
  // the combination instead of accepting it and dropping it. The type check alone is not enough: a
  // nurture-capable type can be re-anchored to a per-recipient trigger after the fact, which is
  // exactly the shape of the reported defect.
  const ruleValue = watch('Rule')
  const gateAppliesToRule = !isRecipientAnchoredRule(ruleValue)
  const isNurtureCapable =
    !!(emailTypeKey && lookup.data?.EngagementTargetsByEmailTypeKey?.[emailTypeKey]) && gateAppliesToRule

  // Reset the form when the panel reopens for a different touchpoint / create.
  useEffect(() => {
    if (!isOpen) return
    reset(buildDefaults(touchPoint, allFileImportIds))
    setSelectedAsset(null)
    setAppliedDefault(null)
    setEmailTypeKey(null)
    setView('edit')
    setSaveRefusal(null)
  }, [isOpen, touchPoint, allFileImportIds, reset])

  // Pre-select the company's default sending domain on a NEW activation once the verified-domains
  // lookup loads (the lookup carries IsDefault, mirroring the reply-to default). Only fills a BLANK
  // field and never on edit, so it never overrides a saved or user-picked domain.
  useEffect(() => {
    if (!isOpen || isEdit || fromSendingDomainId) return
    const defaultDomain = domains.data?.find(d => d.IsDefault)
    if (defaultDomain) setValue('FromSendingDomainId', defaultDomain.Id, { shouldValidate: true })
  }, [isOpen, isEdit, domains.data, fromSendingDomainId, setValue])

  // On edit, fetch the bound asset's detail once to populate emailTypeKey so the engagement gate can
  // decide whether to render. (On create, emailTypeKey is set when the asset is picked.) Cancelled via
  // a flag so a fast reopen-for-a-different-touchpoint never lands a stale key.
  useEffect(() => {
    if (!isOpen || !isEdit) return
    const boundAssetId = touchPoint?.EmailProperties?.EmailAssetId
    if (!boundAssetId) return
    let cancelled = false
    void assetApi
      .getAssetDetail(clientCompanyId, workspaceId, campaignId, boundAssetId)
      .then(detail => {
        // Bail if cancelled OR the user has since picked a different asset (onPickAsset owns the key in
        // that case) — otherwise a slow bound-asset fetch could overwrite the freshly-picked type.
        if (cancelled || detail.error || !detail.data || boundAssetId !== getValues('EmailAssetId')) return
        setEmailTypeKey(detail.data.EmailTypeKey ?? null)
        setHasSecondSubject((detail.data.SubjectVariants?.length ?? 0) > 0)
      })
    return () => { cancelled = true }
  }, [isOpen, isEdit, touchPoint, clientCompanyId, workspaceId, campaignId])

  // If the picked type is non-nurture (e.g. the user switches to one), force the engagement gate back to
  // null so a stale stage never persists. Guard on the type being DEFINITIVELY resolved — both the
  // asset's emailTypeKey and the rule lookup loaded — so we never wipe a saved stage during the async
  // edit-open window (where emailTypeKey is briefly null and isNurtureCapable is transiently false).
  useEffect(() => {
    const typeResolved = !!emailTypeKey && !!lookup.data
    if (typeResolved && !isNurtureCapable && watch('EngagementTarget') != null) {
      setValue('EngagementTarget', null)
    }
  }, [emailTypeKey, lookup.data, isNurtureCapable, setValue, watch])

  // ORB-508: re-anchoring to a per-recipient trigger clears the gate immediately, without waiting on
  // the async type resolution the effect above guards for. That guard exists so a slow edit-open never
  // wipes a saved stage; here the user has just changed the anchor themselves, so the intent is
  // unambiguous and the save would be rejected otherwise.
  useEffect(() => {
    if (!gateAppliesToRule && watch('EngagementTarget') != null) {
      setValue('EngagementTarget', null)
    }
  }, [gateAppliesToRule, setValue, watch])

  // Esc to close + focus management.
  useEffect(() => {
    if (!isOpen) return
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) onClose()
    }
    document.addEventListener('keydown', handleEscape)
    panelRef.current?.focus()
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, isSubmitting, onClose])

  // ORB-303: the versions of the chosen email, to say which one this activation sends and whether it may. A saved
  // activation sends the version it names; a newly chosen email sends its latest, which choosing it takes.
  const [emailVersions, setEmailVersions] = useState<AssetVersionSummaryDto[] | null>(null)
  useEffect(() => {
    setEmailVersions(null)
    if (!isOpen || !assetId) return
    let cancelled = false
    void assetApi.getAssetVersions(clientCompanyId, workspaceId, assetId).then(response => {
      if (cancelled || !response.data) return
      setEmailVersions(response.data.map(version => ({
        AssetId: version.AssetId,
        VersionNumber: version.VersionNumber,
        SignedOffAt: version.SignOff?.SignedOffAt ?? null,
      })))
    })
    return () => { cancelled = true }
  }, [isOpen, assetId, clientCompanyId, workspaceId])
  const keepsSavedVersion = isEdit && !!assetId && assetId === touchPoint?.EmailProperties?.EmailAssetId
  const latestVersionNumber = emailVersions?.reduce((highest, version) => Math.max(highest, version.VersionNumber), 0) ?? 0
  const sentVersionNumber = keepsSavedVersion
    ? touchPoint!.EmailProperties!.EmailAssetVersionNumber
    : latestVersionNumber > 0 ? latestVersionNumber : null
  const versionState = emailVersions && sentVersionNumber != null ? activationVersionState(sentVersionNumber, emailVersions) : null

  // ----- Asset selection ---------------------------------------------------

  const onPickAsset = async (asset: WebAssetListItemDto, onChange: (value: string) => void) => {
    onChange(asset.Id)
    setSelectedAsset(asset)

    // Fetch the asset detail to read EmailTypeKey — needed on both create (rule pre-fill + gate) and
    // edit (gate visibility). The persisted rule is preserved on edit, so we only seed defaults below
    // when creating.
    const detail = await assetApi.getAssetDetail(clientCompanyId, workspaceId, campaignId, asset.Id)
    if (detail.error || !detail.data) return
    const pickedTypeKey = detail.data.EmailTypeKey ?? null
    setEmailTypeKey(pickedTypeKey)
    setHasSecondSubject((detail.data.SubjectVariants?.length ?? 0) > 0)

    if (isEdit) return // preserve persisted rule + engagement target on edit
    if (!pickedTypeKey) return

    const suggested = lookup.data?.DefaultsByEmailTypeKey?.[pickedTypeKey]
    if (suggested) {
      setValue('Rule', suggested, { shouldValidate: true })
      setAppliedDefault({ emailTypeKey: pickedTypeKey, rule: suggested })
    }

    // ORB-359: (re)set the engagement gate from the new asset's type default — null when the type has no
    // entry — so switching from a nurture asset to a non-nurture one never leaves a stale stage behind.
    const engagementDefault = lookup.data?.EngagementTargetsByEmailTypeKey?.[pickedTypeKey] ?? null
    setValue('EngagementTarget', engagementDefault, { shouldValidate: true })
  }

  const onClearAsset = (onChange: (value: string) => void) => {
    onChange('')
    setSelectedAsset(null)
    setAppliedDefault(null)
    setEmailTypeKey(null)
    setHasSecondSubject(null)
    setValue('EngagementTarget', null)
    // With no asset, the mobile Edit/Preview toggle disappears (gated on assetId); snap back to the
    // edit pane so a Preview-view user is never stranded with no way back to the rail.
    setView('edit')
  }

  // ----- Sending domain forecast (ORB-953) -----------------------------------

  // Asked with the domain, rule and lists Save would send, and only once they are complete, so the
  // forecast never answers a form Save would refuse for another reason. Every other field is left out,
  // so editing it asks nothing.
  const forecastRequest: SendingDomainForecastRequestDto | null =
    isOpen && assetId && !lockedByBlast && fromSendingDomainId && validateRule(rule) === true
      && (rule.Kind !== EmailSendRuleKind.Scheduled || selectedListCount > 0)
      ? {
          TouchPointId: touchPoint?.Id ?? null,
          FromSendingDomainId: fromSendingDomainId,
          Rule: forecastRule(rule),
          TargetFileImportIds: sameStringSet(targetFileImportIds ?? [], allFileImportIds)
            ? null
            : [...(targetFileImportIds ?? [])].sort(),
        }
      : null
  const domainForecast = useSendingDomainForecast(workspaceId, webId, forecastRequest)
  // Domain facts label the select from the last answer, even while a newer one is on its way; what the
  // answer says about this email counts only when it answers the form as it is now.
  const domainFacts = domainForecast.latest
  const selectedDomainFacts = domainFacts?.Domains.find(d => d.Id === fromSendingDomainId) ?? null
  const forecastNow = domainForecast.current
  const domainRefusal = forecastNow && !forecastNow.CanSave ? forecastNow : null
  const warmupPlan = forecastNow?.Forecast
  const spreadPlan = !domainRefusal && isSpreadByWarmup(warmupPlan) ? warmupPlan : null
  const switchTo = forecastNow ? readyDomainToSwitchTo(forecastNow) : null
  const notSentOnSave = !domainRefusal && forecastNow ? saveConsequence(forecastNow) : null
  const spreadLine = !domainRefusal && forecastNow ? willFireSpreadLine(forecastNow) : null
  const domainOptions = domainFacts
    ? domainFacts.Domains.map(d => ({
        id: d.Id,
        label: domainOptionLabel(d, domainFacts.WarmupLastStep),
        selectable: isDomainSelectable(d),
      }))
    : (domains.data ?? []).map(d => ({ id: d.Id, label: d.DisplayName, selectable: true }))
  const domainDescribedBy = [
    selectedDomainFacts && 'domain-status',
    domainRefusal && 'domain-limit-banner',
    spreadPlan && 'warmup-banner',
  ].filter(Boolean).join(' ')

  const switchDomain = (domainId: string) => {
    setValue('FromSendingDomainId', domainId, { shouldValidate: true, shouldDirty: true })
    // The banner holding the Switch button goes away with the old domain, so focus lands on the select.
    domainSelectRef.current?.focus()
  }

  // ----- Save --------------------------------------------------------------

  // Gate on an explicit normalized diff vs. the original rather than RHF's isDirty: isDirty stays
  // sticky for the Controller-managed Rule object and won't clear when the user reverts to the
  // original value. This compare is nullish-tolerant and re-disables Save on a true revert.
  const baseline = buildDefaults(touchPoint, allFileImportIds)
  const dirty = !sameActivation(
    {
      EmailAssetId: assetId,
      FromSendingDomainId: fromSendingDomainId,
      ComplianceClass: complianceClass,
      EngagementTarget: engagementTarget,
      ReplyToEmail: replyToEmail,
      TargetFileImportIds: targetFileImportIds,
      Rule: rule,
      SplitTestEnabled: splitTestEnabled,
      SplitTestCohortPercent: splitTestCohortPercent,
      SplitTestWindowHours: splitTestWindowHours,
      SplitTestWinMetric: splitTestWinMetric,
    },
    baseline
  )
  const canSave = isValid && dirty && !isSubmitting && !lockedByBlast && !domainRefusal

  // A 422 keeps its messages beside Save, and the forecast is asked again: a domain that changed state
  // since the last answer is the usual reason Save disagrees with it.
  const keepRefusalOnScreen = (response: { status: number; error?: string }) => {
    if (response.status !== 422) return
    const messages = [...new Set(parseValidationErrors({ error: response.error }).map(error => error.Message))]
    if (messages.length > 0) setSaveRefusal(messages)
    domainForecast.refresh()
  }

  const onValid = async (data: ActivationFormData) => {
    setSaveRefusal(null)
    const name = autoDeriveName(selectedAsset, data.Rule, touchPoint)
    const emailProperties = {
      EmailAssetId: data.EmailAssetId,
      Rule: data.Rule,
      FromSendingDomainId: data.FromSendingDomainId || null,
      // null lets the server smart-default the consent class (ORB-340).
      ComplianceClass: data.ComplianceClass ?? null,
      // null/None = no engagement gate (ORB-359).
      EngagementTarget: data.EngagementTarget ?? null,
      ReplyToEmail: data.ReplyToEmail.trim() || null,
      // A full selection collapses to null = "all included file imports, dynamically" — a file added
      // to the Web later is then automatically included. A strict subset persists as explicit ids,
      // frozen to that choice (ORB-347, ORB-378, ORB-443).
      TargetFileImportIds: sameStringSet(data.TargetFileImportIds, allFileImportIds)
        ? null
        : data.TargetFileImportIds,
      // A/B subject split test (ORB-453) — only sent when enabled; the server ignores it for
      // non-scheduled sends and single-subject copies.
      SubjectSplitTest: data.SplitTestEnabled
        ? {
            Enabled: true,
            TestCohortPercent: data.SplitTestCohortPercent,
            MeasurementWindowMinutes: data.SplitTestWindowHours * 60,
            WinMetric: data.SplitTestWinMetric,
          }
        : null,
    }

    if (isEdit) {
      const res = await touchPointsApi.updateTouchPoint(workspaceId, touchPoint!.Id, {
        Name: name,
        ETag: touchPoint!.ETag,
        EmailProperties: emailProperties,
      })
      if (res.error || !res.data) {
        keepRefusalOnScreen(res)
        showErrorUnless422('Save failed', res, 'Unable to save activation')
        return
      }
      showSuccess('Activation saved', 'The email activation has been updated.')
    } else {
      const res = await touchPointsApi.createTouchPoint(workspaceId, {
        CampaignId: campaignId,
        WebId: webId,
        Name: name,
        Type: TouchPointType.Email,
        EmailProperties: emailProperties,
      })
      if (res.error || !res.data) {
        keepRefusalOnScreen(res)
        showErrorUnless422('Create failed', res, 'Unable to create activation')
        return
      }
      showSuccess('Activation created', 'The email activation is configured.')
    }
    onSaved()
    onClose()
  }

  // Send a one-off test of the selected copy to the logged-in user (ORB-362). Runs the real render +
  // Resend path server-side; the recipient is always the authenticated caller, never sent from here.
  const handleSendTest = async () => {
    if (!fromSendingDomainId || !assetId) return
    // Reply-To now comes from a trusted list (a configured address or '' = use company default), so
    // there's nothing to validate here — '' is valid and tells the server to fall back to the default.
    const trimmedReplyTo = replyToEmail?.trim()
    // ORB-924: the class this touchpoint would send as (its override, else the trigger's default), so the test's
    // unsubscribe footer matches the real send. A rule still being edited resolves to none, and the link is removed.
    let testComplianceClass: EmailComplianceClass | undefined
    try {
      testComplianceClass = complianceClass ?? resolveComplianceClass(rule)
    } catch {
      testComplianceClass = undefined
    }
    setSendingTest(true)
    try {
      const res = await assetApi.sendTestEmail(clientCompanyId, workspaceId, campaignId, assetId, {
        FromSendingDomainId: fromSendingDomainId,
        ReplyToEmail: trimmedReplyTo || undefined,
        ComplianceClass: testComplianceClass,
        // The version the activation sends, as its real sends would render it (ORB-303).
        VersionNumber: sentVersionNumber ?? undefined,
      })
      if (res.data?.Sent) {
        showSuccess('Test email sent', `Sent to ${recipientEmail ?? 'your address'}.`)
      } else {
        // FailureReason rides as the fallback rather than taking priority: the two are mutually
        // exclusive in practice — a 200 carrying Sent:false has no `error`, and an error status has
        // no `data` — so the rendered message is unchanged, and the 422 case now stays quiet.
        showErrorUnless422('Test not sent', res, res.data?.FailureReason || 'Could not send the test email.')
      }
    } catch {
      showError('Test not sent', 'Could not send the test email.')
    } finally {
      setSendingTest(false)
    }
  }

  if (!isOpen) return null

  return (
    <div
      ref={panelRef}
      tabIndex={-1}
      className="fixed inset-0 z-50 flex flex-col bg-white dark:bg-slate-900 focus:outline-none"
      role="dialog"
      aria-modal="true"
      aria-labelledby="email-activation-title"
    >
      {/* Top bar (flex-shrink-0) */}
      <header className="flex-shrink-0 flex items-start justify-between gap-3 px-4 sm:px-6 pt-4 pb-3 sm:pt-5 sm:pb-4 border-b border-slate-200 dark:border-slate-800">
        <div className="min-w-0">
          <div className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Email activation
          </div>
          <h2
            id="email-activation-title"
            className="mt-0.5 text-lg font-semibold text-slate-900 dark:text-slate-100 truncate"
          >
            {isEdit ? touchPoint!.Name : 'New activation'}
          </h2>
        </div>
        <div className="flex items-center gap-3">
          {/* Edit/Preview switch — only meaningful below md, where the two panes collapse to one. Hidden
              at md+ (both panes visible) and when no asset is picked (nothing to preview). */}
          {assetId && (
            <div className="md:hidden">
              <SegmentedToggle
                ariaLabel="Switch between editing and preview"
                value={view}
                onChange={setView}
                options={[
                  { value: 'edit', label: 'Edit', icon: <Pencil className="h-3.5 w-3.5" aria-hidden="true" /> },
                  { value: 'preview', label: 'Preview', icon: <Eye className="h-3.5 w-3.5" aria-hidden="true" /> },
                ]}
              />
            </div>
          )}
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 rounded p-1 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1"
            aria-label="Close panel"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Two-pane body (flex-1 min-h-0): config rail (left) + live preview (right).
          Below md only one shows at a time, gated on `view`; at md+ both show side by side. */}
      <div className="flex-1 flex min-h-0">
        {/* Left config rail — visible always at md+, gated on `view` below md */}
        <div
          className={`${view === 'edit' ? 'flex' : 'hidden'} md:flex flex-col w-full md:w-[400px] lg:w-[440px] shrink-0 overflow-y-auto border-r border-slate-200 dark:border-slate-800 px-4 sm:px-6 py-6 space-y-8`}
        >
            {(lockedByBlast || isDisarmed) && (
              <ActivationLockBanner
                blast={blast ?? null}
                blastHref={blastHref}
                blastTerminal={blastTerminal}
                canStartOver={canStartOver}
                isDisarmed={isDisarmed}
                onStartOver={onStartOver}
                onReschedule={onReschedule}
                startingOver={startingOver}
                rescheduling={rescheduling}
              />
            )}
            {/* When a blast is staged the whole config is locked (ORB-378) — fieldset disables every
                native control; Save is separately gated. Disarmed activations stay editable. */}
            <fieldset disabled={lockedByBlast} className={`space-y-8 border-0 p-0 m-0 min-w-0 ${lockedByBlast ? 'opacity-60' : ''}`}>
            {/* Section 1 — Pick an email */}
            <section className="space-y-3">
              <SectionLabel>Pick an email</SectionLabel>
              <Controller
                control={control}
                name="EmailAssetId"
                rules={{ required: true }}
                render={({ field }) => (
                  <EmailAssetSelect
                    clientCompanyId={clientCompanyId}
                    workspaceId={workspaceId}
                    campaignId={campaignId}
                    webId={webId}
                    selectedAssetId={field.value || null}
                    usedAssetIds={usedAssetIds}
                    onSelect={asset => { void onPickAsset(asset, field.onChange) }}
                    onClear={() => onClearAsset(field.onChange)}
                    onNavigateToAssets={onNavigateToAssets}
                  />
                )}
              />
              {versionState && <ActivationVersionNote state={versionState} isNew={!keepsSavedVersion} />}
            </section>

            {/* Send from — sender identity, broken into clearly-labeled sub-blocks (ORB-365):
                (a) sending domain, (b) consent class, (c) reply-to. Audience targeting (the
                engagement gate) lives in its own section below, not here. */}
            {assetId && (
              <section className="space-y-4">
                <SectionLabel>Send from</SectionLabel>

                {/* (a) Sending domain. The verified-domain lookup fills the first list and the default;
                    the forecast then labels every company domain with its warm-up state (ORB-953). */}
                <div className="space-y-2">
                  <div className="space-y-1">
                    <label
                      htmlFor="from-sending-domain"
                      className="block text-xs font-medium text-slate-700 dark:text-slate-300"
                    >
                      Sending domain
                    </label>
                    <Controller
                      control={control}
                      name="FromSendingDomainId"
                      rules={{ required: true }}
                      render={({ field }) => (
                        <select
                          id="from-sending-domain"
                          ref={domainSelectRef}
                          value={field.value}
                          onChange={e => field.onChange((e.target as HTMLSelectElement).value)}
                          onBlur={field.onBlur}
                          aria-invalid={domainRefusal ? true : undefined}
                          aria-describedby={domainDescribedBy || undefined}
                          className={`w-full rounded-md border ${domainRefusal ? 'border-red-300 dark:border-red-700' : 'border-slate-300 dark:border-slate-700'} bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500`}
                        >
                          <option value="" disabled>
                            {domains.isLoading ? 'Loading domains…' : 'Select a sending domain'}
                          </option>
                          {domainOptions.map(option => (
                            <option key={option.id} value={option.id} disabled={!option.selectable}>{option.label}</option>
                          ))}
                        </select>
                      )}
                    />
                    {domains.error && (
                      <p className="text-xs text-red-600 dark:text-red-400">{domains.error}</p>
                    )}
                    {!domains.error && !domains.isLoading && (domains.data?.length ?? 0) === 0 && (
                      <SettingsNudge
                        isAdmin={isCompanyAdmin}
                        onGo={() => goToSettings('sending-domains')}
                        noun="verified sending domains"
                        cta="Add a sending domain"
                      />
                    )}
                    {selectedDomainFacts && domainFacts && (
                      <SendingDomainStateLines
                        id="domain-status"
                        domain={selectedDomainFacts}
                        lastStep={domainFacts.WarmupLastStep}
                        rateLimits={domainFacts.RateLimits}
                      />
                    )}
                    {domainForecast.error && (
                      <p className="text-xs text-red-600 dark:text-red-400">
                        Couldn't check how this domain would send this email: {domainForecast.error}
                      </p>
                    )}
                  </div>

                  {domainRefusal && (
                    <SendingDomainRefusalBanner
                      id="domain-limit-banner"
                      forecast={domainRefusal}
                      switchTo={switchTo}
                      onSwitch={switchDomain}
                    />
                  )}
                  {spreadPlan && forecastNow && (
                    <SendingDomainSpreadBanner
                      id="warmup-banner"
                      forecast={forecastNow}
                      plan={spreadPlan}
                      switchTo={switchTo}
                      onSwitch={switchDomain}
                    />
                  )}
                  {selectedDomainFacts?.State === 'Warming' && <RegistrantEmailsNote besideEstimate={!!spreadPlan} />}
                </div>

                {/* (b) Consent class (ORB-340/365) — resolved chip with override/reset */}
                <Controller
                  control={control}
                  name="ComplianceClass"
                  render={({ field }) => (
                    <ConsentClassChip value={field.value} onChange={field.onChange} rule={rule} />
                  )}
                />

                {/* (c) Reply-To picker (ORB-364) — a select over the company's configured reply-to addresses.
                    '' = follow the company default (the server falls back blank→default at send time);
                    any other value is an explicit address. No free-form entry — addresses are managed in
                    Settings → Reply-To Addresses. */}
                <Controller
                  control={control}
                  name="ReplyToEmail"
                  render={({ field }) => {
                    const options = replyToAddresses.data ?? []
                    const noAddresses = !replyToAddresses.isLoading && !replyToAddresses.error && options.length === 0
                    const defaultEmail = options.find(o => o.IsDefault)?.Value
                    const defaultLabel = defaultEmail
                      ? `Use company default (${defaultEmail})`
                      : 'Use company default'
                    // A previously-saved address may no longer be in the book (it was removed since). Keep it
                    // as a selectable option so the select stays on the saved value instead of silently
                    // snapping to "Use company default" — the user must explicitly change it.
                    const savedMissing = !!field.value && !options.some(o => o.Value === field.value)
                    return (
                      <div className="space-y-1">
                        <label
                          htmlFor="reply-to-email"
                          className="block text-xs font-medium text-slate-700 dark:text-slate-300"
                        >
                          Reply-To <span className="font-normal text-slate-400 dark:text-slate-500">(optional)</span>
                        </label>
                        <select
                          id="reply-to-email"
                          value={field.value}
                          onChange={e => field.onChange((e.target as HTMLSelectElement).value)}
                          onBlur={field.onBlur}
                          disabled={noAddresses && !savedMissing}
                          className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-60"
                          aria-label="Reply-To address"
                        >
                          <option value="">
                            {replyToAddresses.isLoading ? 'Loading addresses…' : defaultLabel}
                          </option>
                          {options.map(o => (
                            <option key={o.Value} value={o.Value}>{o.DisplayName}</option>
                          ))}
                          {savedMissing && (
                            <option value={field.value}>{field.value} (no longer configured)</option>
                          )}
                        </select>
                        {replyToAddresses.error && (
                          <p className="text-xs text-red-600 dark:text-red-400">{replyToAddresses.error}</p>
                        )}
                        {noAddresses && !savedMissing && (
                          <SettingsNudge
                            isAdmin={isCompanyAdmin}
                            onGo={() => goToSettings('reply-to-addresses')}
                            noun="reply-to addresses"
                            cta="Add reply-to addresses"
                          />
                        )}
                      </div>
                    )
                  }}
                />

                {/* Send a test to yourself (ORB-362) — enabled once a verified domain is picked */}
                <div className="space-y-1.5 border-t border-slate-200 dark:border-slate-800 pt-3">
                  <button
                    type="button"
                    onClick={handleSendTest}
                    disabled={!fromSendingDomainId || sendingTest}
                    className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="h-3.5 w-3.5" aria-hidden="true" />
                    {sendingTest ? 'Sending…' : 'Send test to me'}
                  </button>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {fromSendingDomainId
                      ? `Sends this email to ${recipientEmail ?? 'your address'} from the selected domain. Runs the real send path; not tracked.`
                      : 'Select a verified sending domain above to send yourself a test.'}
                  </p>
                </div>
              </section>
            )}

            {/* Audience targeting — engagement gate (ORB-359/365). Its own section, separate from sender
                identity, and only for nurture-capable email types (a non-None default in the lookup). */}
            {assetId && isNurtureCapable && (
              <section className="space-y-2">
                <SectionLabel>Who it reaches</SectionLabel>
                <Controller
                  control={control}
                  name="EngagementTarget"
                  render={({ field }) => (
                    <EngagementTargetSelect value={field.value} onChange={field.onChange} />
                  )}
                />
              </section>
            )}

            {/* Section 2 — When it sends */}
            {assetId && (
              <section className="space-y-4">
                <SectionLabel>When it sends</SectionLabel>
                <Controller
                  control={control}
                  name="Rule"
                  rules={{ validate: validateRule }}
                  render={({ field }) => (
                    <div className="space-y-4">
                      <SentenceRuleEditor
                        value={field.value}
                        onChange={field.onChange}
                        lookup={lookup.data}
                        isLoading={lookup.isLoading}
                        hideScheduledTime
                        defaultTimeZone={defaultTimeZone}
                      />

                      {/* Tz-aware send time — Scheduled rules only (ORB-352) */}
                      {field.value.Kind === EmailSendRuleKind.Scheduled && (
                        <ScheduledSendTimeFields
                          value={field.value}
                          onChange={field.onChange}
                          error={errors.Rule?.message}
                        />
                      )}
                    </div>
                  )}
                />

                {errors.Rule && rule.Kind !== EmailSendRuleKind.Scheduled && (
                  <p className="text-xs text-red-600 dark:text-red-400">{errors.Rule.message}</p>
                )}

                {appliedDefault && rulesEqual(rule, appliedDefault.rule) && !errors.Rule && (
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Default for{' '}
                    <code className="px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                      {appliedDefault.emailTypeKey}
                    </code>
                    {' '}— edit any underlined token to override.
                  </p>
                )}
              </section>
            )}

            {/* Who it targets — file-import subset (ORB-347, ORB-443). Scheduled rules only:
                event-anchored sends fan out per-recipient and target the whole audience. A new send
                starts unpicked so the audience is an explicit choice, never a pre-checked default. */}
            {assetId && rule.Kind === EmailSendRuleKind.Scheduled && (
              <section className="space-y-2">
                <SectionLabel>Who it targets</SectionLabel>

                {/* Plain-language state so the audience never reads as auto/fixed (ORB-443). */}
                {totalListCount > 0 && (
                  selectedListCount === 0 ? (
                    <p className="text-xs font-medium text-amber-600 dark:text-amber-400">
                      Choose at least one list for this email to send to.
                    </p>
                  ) : allListsSelected ? (
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {totalListCount === 1
                        ? 'Sending to the only list on this page.'
                        : `Sending to all ${totalListCount} lists — lists added to this page later are included automatically.`}
                    </p>
                  ) : (
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Sending to {selectedListCount} of {totalListCount} lists.
                    </p>
                  )
                )}

                <Controller
                  control={control}
                  name="TargetFileImportIds"
                  rules={{
                    validate: (value: string[], formValues: ActivationFormData) =>
                      formValues.Rule?.Kind !== EmailSendRuleKind.Scheduled ||
                      (value?.length ?? 0) >= 1 ||
                      'Select at least one list to send to.',
                  }}
                  render={({ field }) => (
                    <MultiSelect
                      options={fileImportOptions}
                      value={field.value}
                      onChange={selected => field.onChange(selected as string[])}
                      disabled={lockedByBlast}
                      emptyMessage="This Web has no included file imports yet."
                    />
                  )}
                />
              </section>
            )}

            {/* Subject A/B test (ORB-453). Scheduled sends only; offered when the copy has two subjects. */}
            {assetId && rule.Kind === EmailSendRuleKind.Scheduled && hasSecondSubject === true && (
              <section className="space-y-3">
                <SectionLabel>Subject A/B test</SectionLabel>
                <ToggleRow
                  label="Split test subject lines"
                  checked={splitTestEnabled}
                  onChange={v => setValue('SplitTestEnabled', v, { shouldValidate: true })}
                  disabled={lockedByBlast}
                  size="sm"
                />
                {splitTestEnabled && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <NumberInput
                        name="SplitTestCohortPercent"
                        label="Test cohort (%)"
                        labelSize="xs"
                        min={1}
                        max={50}
                        step={1}
                        value={splitTestCohortPercent}
                        disabled={lockedByBlast}
                        onChange={(e: Event) => {
                          // Clamp to the valid 1–50 range so a cleared or out-of-range field can't put an
                          // invalid value into form state and submit (the server would 422 it otherwise).
                          const raw = Number((e.target as HTMLInputElement).value)
                          const next = Number.isFinite(raw) ? Math.min(50, Math.max(1, raw)) : 1
                          setValue('SplitTestCohortPercent', next, { shouldValidate: true })
                        }}
                      />
                      <NumberInput
                        name="SplitTestWindowHours"
                        label="Measure for (hours)"
                        labelSize="xs"
                        min={1}
                        step={1}
                        value={splitTestWindowHours}
                        disabled={lockedByBlast}
                        onChange={(e: Event) => {
                          // Clamp to at least 1 hour so a cleared field can't submit an invalid window.
                          const raw = Number((e.target as HTMLInputElement).value)
                          const next = Number.isFinite(raw) ? Math.max(1, raw) : 1
                          setValue('SplitTestWindowHours', next, { shouldValidate: true })
                        }}
                      />
                    </div>
                    <RadioGroup
                      name="SplitTestWinMetric"
                      label="Pick the winner by"
                      layout="grid"
                      options={[
                        { value: SplitTestWinMetric.Opens, label: 'Opens', description: 'Higher open rate wins', disabled: lockedByBlast },
                        { value: SplitTestWinMetric.Clicks, label: 'Clicks', description: 'Higher click rate wins', disabled: lockedByBlast },
                      ]}
                      value={splitTestWinMetric}
                      onChange={v => setValue('SplitTestWinMetric', (v as SplitTestWinMetric) ?? SplitTestWinMetric.Opens, { shouldValidate: true })}
                    />
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      After the window, the winning subject sends to the rest. If neither wins clearly, or the cohort is too small, Subject A sends.
                    </p>
                  </div>
                )}
              </section>
            )}

            {/* One-subject hint (ORB-453): guide back to the builder instead of offering the test. */}
            {assetId && rule.Kind === EmailSendRuleKind.Scheduled && hasSecondSubject === false && (
              <section className="space-y-2">
                <SectionLabel>Subject A/B test</SectionLabel>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  This email has a single subject. Add a second subject in the email copy to split test the subject line.
                </p>
              </section>
            )}

            {/* Section 3 — Preview line (separate from sentence; reinforces the read) */}
            {assetId && (
              <section className="rounded-md border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 px-4 py-3">
                <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Will fire
                </div>
                <div className="mt-1 text-sm text-slate-800 dark:text-slate-200">
                  {formatEmailRule(rule)}
                </div>
                {spreadLine && (
                  <div className="mt-0.5 text-[13px] leading-5 text-slate-600 dark:text-slate-400">{spreadLine}</div>
                )}
              </section>
            )}
            </fieldset>
          </div>

          {/* Right preview pane — visible always at md+, gated on `view` below md. Below md it takes the
              full width when shown (Preview selected); the rail is hidden in that state. */}
          <div className={`${view === 'preview' ? 'flex' : 'hidden'} md:flex flex-1 min-w-0 bg-slate-100 dark:bg-slate-950`}>
            {assetId ? (
              <div className="flex-1 min-w-0 h-full">
                <EmailCopyPreview
                  clientCompanyId={clientCompanyId}
                  workspaceId={workspaceId}
                  campaignId={campaignId}
                  assetId={assetId}
                  versionNumber={sentVersionNumber}
                />
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center text-center px-8">
                <div>
                  <Mail className="w-10 h-10 mx-auto text-slate-300 dark:text-slate-600" />
                  <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">Pick an email to preview it here.</p>
                </div>
              </div>
            )}
          </div>
      </div>

      {/* Footer (flex-shrink-0) */}
      <footer className="flex-shrink-0 space-y-3 px-4 sm:px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60">
        {saveRefusal && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[13px] leading-5 text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
          >
            <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <div className="min-w-0 space-y-0.5">
              <p className="font-semibold">Not saved</p>
              {saveRefusal.map(message => <p key={message}>{message}</p>)}
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-end gap-2">
          {domainRefusal ? (
            <p id="save-blocked-reason" className="mr-2 flex items-center gap-1.5 text-[13px] leading-5 text-red-700 dark:text-red-400">
              <CircleAlert className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {saveBlockedReason(domainRefusal)}
            </p>
          ) : notSentOnSave && (
            <p id="save-consequence" className="mr-2 flex items-center gap-1.5 text-[13px] leading-5 text-amber-800 dark:text-amber-300">
              <TriangleAlert className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {notSentOnSave}
            </p>
          )}
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-slate-50 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 disabled:opacity-50"
          >
            Cancel
          </button>
          {/* primary-700, not 600: white text on 600 misses AA contrast (ORB-953 design pass). */}
          <button
            type="button"
            onClick={handleSubmit(onValid)}
            disabled={!canSave}
            aria-describedby={domainRefusal ? 'save-blocked-reason' : notSentOnSave ? 'save-consequence' : undefined}
            className="inline-flex items-center px-3.5 py-1.5 text-sm font-medium rounded bg-primary-700 hover:bg-primary-800 text-white shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 disabled:bg-slate-300 dark:disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Saving…' : isEdit ? 'Save changes' : 'Create activation'}
          </button>
        </div>
      </footer>
    </div>
  )
}

function SectionLabel({ children }: { children: preact.ComponentChildren }) {
  return (
    <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
      {children}
    </div>
  )
}

function autoDeriveName(
  asset: WebAssetListItemDto | null,
  rule: EmailSendRuleDto,
  touchPoint: TouchPointViewDto | null
): string {
  if (touchPoint?.Name && touchPoint.Name.trim().length > 0) {
    return touchPoint.Name
  }
  const base = asset?.Name?.trim() || 'Email activation'
  return `${base} — ${formatEmailRule(rule)}`
}

/** Nullish-tolerant field compare for the send rule — drives the dirty check + default caption. */
function rulesEqual(a: EmailSendRuleDto, b: EmailSendRuleDto | null | undefined): boolean {
  if (!b) return false
  return (
    a.Kind === b.Kind &&
    (a.SendAtAbsolute ?? null) === (b.SendAtAbsolute ?? null) &&
    (a.EventType ?? null) === (b.EventType ?? null) &&
    (a.OffsetValue ?? null) === (b.OffsetValue ?? null) &&
    (a.OffsetUnit ?? null) === (b.OffsetUnit ?? null) &&
    (a.ScheduledSendLocalTime ?? null) === (b.ScheduledSendLocalTime ?? null) &&
    (a.ScheduledSendTimeZone ?? null) === (b.ScheduledSendTimeZone ?? null) &&
    (a.ScheduledSendMode ?? null) === (b.ScheduledSendMode ?? null)
  )
}

/** The rule as Save sends it, in a fixed field order so an equal rule always asks the same forecast. */
function forecastRule(rule: EmailSendRuleDto): EmailSendRuleDto {
  return {
    Kind: rule.Kind,
    SendAtAbsolute: rule.SendAtAbsolute ?? null,
    ScheduledSendLocalTime: rule.ScheduledSendLocalTime ?? null,
    ScheduledSendTimeZone: rule.ScheduledSendTimeZone ?? null,
    ScheduledSendMode: rule.ScheduledSendMode ?? null,
    EventType: rule.EventType ?? null,
    OffsetValue: rule.OffsetValue ?? null,
    OffsetUnit: rule.OffsetUnit ?? null,
  }
}

/** Order-insensitive string-set compare for the target file-import selection. */
function sameStringSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false
  const setB = new Set(b)
  return a.every(id => setB.has(id))
}

// None and null both mean "no engagement gate" — collapse them so the dirty-check never treats a fresh
// "Everyone" (null) as different from a persisted None.
const gateKey = (t: EmailEngagementTarget | null) =>
  t == null || t === EmailEngagementTarget.None ? null : t

function sameActivation(a: ActivationFormData, b: ActivationFormData): boolean {
  return (
    a.EmailAssetId === b.EmailAssetId &&
    a.FromSendingDomainId === b.FromSendingDomainId &&
    (a.ComplianceClass ?? null) === (b.ComplianceClass ?? null) &&
    gateKey(a.EngagementTarget) === gateKey(b.EngagementTarget) &&
    a.ReplyToEmail === b.ReplyToEmail &&
    sameStringSet(a.TargetFileImportIds, b.TargetFileImportIds) &&
    rulesEqual(a.Rule, b.Rule) &&
    a.SplitTestEnabled === b.SplitTestEnabled &&
    // Config values only matter when the test is on — ignore them when disabled so tweaking a hidden
    // field never falsely enables Save (the disabled path sends SubjectSplitTest: null anyway).
    (!a.SplitTestEnabled || (
      a.SplitTestCohortPercent === b.SplitTestCohortPercent &&
      a.SplitTestWindowHours === b.SplitTestWindowHours &&
      a.SplitTestWinMetric === b.SplitTestWinMetric
    ))
  )
}
